/**
 * useBuildingMapLayer — Capa MapLibre para building_centroids
 *
 * @remarks
 * Implementa renderizado de edificios con clustering nativo de MapLibre.
 * Carga datos por bbox del mapa para nunca traer los 439K puntos completos.
 * V1.2: colorización por qualityStatus, stroke ámbar en requiresReview,
 *       popup con 4 secciones (Identificación / Masa / Morfología / Función),
 *       filtros por calidad, revisión, exclusión, huella, morfología, masa y función.
 *
 * Layers:
 *  - buildings-clusters        : círculos de cluster
 *  - buildings-cluster-count   : label numérico sobre cluster
 *  - buildings-unclustered     : puntos individuales (zoom ≥ 14)
 */

import { useCallback, useEffect, useRef } from 'react';
import type { GeoJSONSource, MapMouseEvent, MapGeoJSONFeature } from 'maplibre-gl';
import { mapEngine } from '../../../map-engine/MapEngineService';
import { buildingsApi } from '../../../buildings/buildings-api';
import {
  useBuildingsLayerStore,
  type BuildingFilters,
  type BuildingViewportStats,
} from '../../../buildings/buildings-layer-store';
import {
  QUALITY_STATUS_COLORS,
} from '../../../buildings/building-v12-labels';

// ─── IDs de source y layers ───────────────────────────────────────────────────

const SOURCE_ID      = 'buildings-source';
const LAYER_CLUSTERS = 'buildings-clusters';     // legacy cleanup only
const LAYER_COUNT    = 'buildings-cluster-count'; // legacy cleanup only
const LAYER_POINTS   = 'buildings-unclustered';
const LAYER_SELECTED = 'buildings-selected';      // highlight ring for selected building

// ─── Paleta ───────────────────────────────────────────────────────────────────

const QUALITY_COLOR_FALLBACK = QUALITY_STATUS_COLORS['unknown'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Construye la expresión de filtro MapLibre para la capa de puntos individuales. */
function buildUnclusteredFilter(f: BuildingFilters): unknown[] {
  const conditions: unknown[] = [];

  if (f.qualityStatus !== 'all') {
    conditions.push(['==', ['get', 'qualityStatus'], f.qualityStatus]);
  }
  if (f.requiresReview === 'only') {
    conditions.push(['==', ['get', 'requiresReview'], true]);
  }
  if (f.hasFootprintGeometry === 'with') {
    conditions.push(['==', ['get', 'hasFootprintGeometry'], true]);
  } else if (f.hasFootprintGeometry === 'without') {
    conditions.push(['==', ['get', 'hasFootprintGeometry'], false]);
  }
  if (f.morphologyClass !== 'all') {
    conditions.push(['==', ['get', 'morphologyClass'], f.morphologyClass]);
  }
  if (f.builtMassClass === 'none') {
    conditions.push(['==', ['get', 'builtMassClass'], null]);
  } else if (f.builtMassClass !== 'all') {
    conditions.push(['==', ['get', 'builtMassClass'], f.builtMassClass]);
  }
  if (f.functionalClass === 'unclassified') {
    conditions.push(['==', ['get', 'functionalClass'], null]);
  } else if (f.functionalClass !== 'all') {
    conditions.push(['==', ['get', 'functionalClass'], f.functionalClass]);
  }
  // V1.2.1 — filtros de revisión
  if (f.reviewStatus === 'reviewed') {
    conditions.push(['==', ['get', 'isUserReviewed'], true]);
  } else if (f.reviewStatus === 'unreviewed') {
    conditions.push(['==', ['get', 'isUserReviewed'], false]);
  }
  if (f.manualEditStatus === 'edited') {
    conditions.push(['==', ['get', 'hasManualOverride'], true]);
  } else if (f.manualEditStatus === 'unedited') {
    conditions.push(['==', ['get', 'hasManualOverride'], false]);
  }
  if (f.effectivePopulationStatus !== 'all') {
    conditions.push(['==', ['get', 'effectivePopulationStatus'], f.effectivePopulationStatus]);
  }

  return ['all', ...conditions];
}

/** Calcula contadores desde los features cargados en el viewport. */
function computeViewportStats(features: GeoJSON.Feature[]): BuildingViewportStats {
  let needsReview = 0, excluded = 0, withFootprint = 0, withBuiltArea = 0, withoutFunctionalClass = 0, manualOverrides = 0;
  for (const f of features) {
    const p = f.properties ?? {};
    if (p['requiresReview']         === true)  needsReview++;
    if (p['excludedFromPopulation'] === true)  excluded++;
    if (p['hasFootprintGeometry']   === true)  withFootprint++;
    if (p['builtAreaEstimatedM2']   != null)   withBuiltArea++;
    if (p['functionalClass']        == null)   withoutFunctionalClass++;
    if (p['hasManualOverride']      === true)  manualOverrides++;
  }
  return { totalLoaded: features.length, needsReview, excluded, withFootprint, withBuiltArea, withoutFunctionalClass, manualOverrides };
}

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface UseBuildingMapLayerOptions {
  projectId: string;
  visible:   boolean;
  filters:   BuildingFilters;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Comprueba si la source de buildings ya existe en el mapa. */
function areBuildingLayersPresent(map: ReturnType<typeof mapEngine.getMap>): boolean {
  if (!map) return false;
  return !!map.getSource(SOURCE_ID);
}

export function useBuildingMapLayer({
  projectId,
  visible,
  filters,
}: UseBuildingMapLayerOptions): void {
  const layersReady            = useRef(false);
  const loadingRef             = useRef(false);
  const visibleRef             = useRef(visible);
  const filtersRef             = useRef(filters);
  const lastStyleDataTimeRef   = useRef<number>(0);
  const setViewportStats        = useBuildingsLayerStore((s) => s.setViewportStats);
  const setViewportFeatures     = useBuildingsLayerStore((s) => s.setViewportFeatures);
  const openBuildingReviewFn    = useBuildingsLayerStore((s) => s.openBuildingReview);
  const openBuildingReviewRef   = useRef(openBuildingReviewFn);
  const reloadTrigger           = useBuildingsLayerStore((s) => s.reloadTrigger);
  const selectedBuilding        = useBuildingsLayerStore((s) => s.selectedBuildingForReview);

  // Mantener ref de openBuildingReview para no re-registrar handlers al cambiar
  useEffect(() => { openBuildingReviewRef.current = openBuildingReviewFn; }, [openBuildingReviewFn]);

  // ── Actualizar highlight del edificio seleccionado ────────────────────────
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersReady.current) return;
    const selectedId = selectedBuilding?.buildingId ?? '';
    if (map.getLayer(LAYER_SELECTED)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.setFilter(LAYER_SELECTED, ['==', ['get', 'id'], selectedId] as any);
    }
  }, [selectedBuilding]);

  // Mantener refs sincronizados para que los callbacks no capturen valores stale
  useEffect(() => { visibleRef.current = visible;  }, [visible]);
  useEffect(() => { filtersRef.current = filters;  }, [filters]);

  // Recargar GeoJSON cuando se guarda/resetea una revisión
  useEffect(() => {
    if (reloadTrigger === 0) return;
    void loadForCurrentBbox();
  // loadForCurrentBbox no cambia entre renders — es useCallback con deps estables
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTrigger]);

  // ── Cargar datos del bbox actual ──────────────────────────────────────────

  const loadForCurrentBbox = useCallback(async (): Promise<void> => {
    if (!projectId || loadingRef.current) return;
    const map = mapEngine.getMap();
    if (!map || !layersReady.current) return;

    loadingRef.current = true;
    try {
      const bounds = map.getBounds();
      const bbox: [number, number, number, number] = [
        bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth(),
      ];

      const fc = await buildingsApi.getGeoJSON(projectId, { bbox, limit: 5000 });
      console.log(`[useBuildingMapLayer] Cargados ${fc.features.length} edificios`);

      const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
      if (source) source.setData(fc);

      setViewportStats(computeViewportStats(fc.features));
      setViewportFeatures(fc.features);
    } catch (err) {
      console.error('[useBuildingMapLayer] Error cargando edificios:', err);
    } finally {
      loadingRef.current = false;
    }
  }, [projectId, setViewportStats, setViewportFeatures]);

  // ── Aplicar filtros cuando cambian (sin recargar datos) ───────────────────

  useEffect(() => {
    if (!layersReady.current) return;
    const map = mapEngine.getMap();
    if (!map) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.setFilter(LAYER_POINTS, buildUnclusteredFilter(filters) as any);
  }, [filters]);

  // ── Inicializar source y layers ───────────────────────────────────────────

  useEffect(() => {
    if (!visible || !projectId) return;

    const map = mapEngine.getMap();
    if (!map) return;

    // ── setup: agrega source + 3 layers + handlers ──────────────────────────
    // Idempotente: comprueba existencia antes de agregar.
    const setup = (): void => {
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
          cluster: false,
        });
      }

      // Capa de puntos individuales (zoom ≥ 14)
      // Color base: qualityStatus. Stroke ámbar si requiresReview = true.
      if (!map.getLayer(LAYER_POINTS)) {
        map.addLayer({
          id: LAYER_POINTS,
          type: 'circle',
          source: SOURCE_ID,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          filter: buildUnclusteredFilter(filtersRef.current) as any,
          minzoom: 14,
          paint: {
            'circle-color': [
              'match', ['get', 'qualityStatus'],
              'high',    QUALITY_STATUS_COLORS['high'],
              'medium',  QUALITY_STATUS_COLORS['medium'],
              'low',     QUALITY_STATUS_COLORS['low'],
              'unknown', QUALITY_STATUS_COLORS['unknown'],
              QUALITY_COLOR_FALLBACK,
            ],
            'circle-radius': [
              'interpolate', ['linear'], ['zoom'],
              14, 5, 16, 7, 18, 10,
            ],
            // Stroke con prioridad: override manual > revisado > requiere revisión > default
            // 1. Azul: edificio con override manual (hasManualOverride)
            // 2. Verde: edificio revisado por usuario (isUserReviewed)
            // 3. Ámbar: requiere revisión del sistema y no fue revisado
            // 4. Blanco: default
            'circle-stroke-color': [
              'case',
              ['==', ['get', 'hasManualOverride'],  true],  '#3b82f6',
              ['==', ['get', 'isUserReviewed'],      true],  '#22c55e',
              ['==', ['get', 'requiresReview'],      true],  '#f59e0b',
              '#ffffff',
            ],
            'circle-stroke-width': [
              'case',
              ['==', ['get', 'hasManualOverride'],  true],  2.5,
              ['==', ['get', 'isUserReviewed'],      true],  2,
              ['==', ['get', 'requiresReview'],      true],  2,
              1.5,
            ],
            'circle-opacity': visibleRef.current ? 0.9 : 0,
          },
        });
      }

      // Capa de highlight: halo cyan sobre el edificio seleccionado
      if (!map.getLayer(LAYER_SELECTED)) {
        map.addLayer({
          id: LAYER_SELECTED,
          type: 'circle',
          source: SOURCE_ID,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          filter: ['==', ['get', 'id'], ''] as any, // vacío al inicio = sin match
          minzoom: 14,
          paint: {
            'circle-color': 'transparent',
            'circle-radius': [
              'interpolate', ['linear'], ['zoom'],
              14, 12, 16, 16, 18, 22,
            ],
            'circle-opacity': 0,
            'circle-stroke-color': '#38bdf8',
            'circle-stroke-width': 2.5,
            'circle-stroke-opacity': 0.95,
          },
        });
      }

      // Aplicar visibilidad actual (por si setup() se llama tras un setStyle() con capa oculta)
      const vis = visibleRef.current ? 'visible' : 'none';
      if (map.getLayer(LAYER_POINTS))   map.setLayoutProperty(LAYER_POINTS,   'visibility', vis);
      if (map.getLayer(LAYER_SELECTED)) map.setLayoutProperty(LAYER_SELECTED, 'visibility', vis);

      layersReady.current = true;
      console.log('[useBuildingMapLayer] Layers listas');
    };

    // ── Click handler: abre directamente el sidebar derecho (sin popup) ───────
    const handlePointClick = (
      e: MapMouseEvent & { features?: MapGeoJSONFeature[] },
    ): void => {
      if (!e.features?.length) return;
      const feature    = e.features[0];
      const props      = (feature.properties ?? {}) as Record<string, unknown>;
      const buildingId = String(props['id'] ?? '');
      if (buildingId) {
        openBuildingReviewRef.current(buildingId, props);
      }
    };

    map.on('click',      LAYER_POINTS, handlePointClick);
    map.on('mouseenter', LAYER_POINTS, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', LAYER_POINTS, () => { map.getCanvas().style.cursor = ''; });

    // ── moveend: recargar bbox ───────────────────────────────────────────────
    const onMoveEnd = (): void => { void loadForCurrentBbox(); };
    map.on('moveend', onMoveEnd);

    // ── styledata: reinyectar layers si el style cambió ──────────────────────
    // MapLibre destruye TODOS los sources/layers custom al llamar map.setStyle().
    // Al recibir styledata con dataType='style', verificamos si nuestro source
    // sobrevivió. Si no, re-ejecutamos setup() + recargamos datos del bbox.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleStyleData = (e: any): void => {
      if (e.dataType !== 'style') return;
      if (!visibleRef.current) return;

      // Throttle: máximo una reinyección por segundo
      const now = Date.now();
      if (now - lastStyleDataTimeRef.current < 1000) return;
      lastStyleDataTimeRef.current = now;

      if (loadingRef.current) return;

      // Si nuestro source ya existe, el style no se reinició (evento espurio)
      if (areBuildingLayersPresent(mapEngine.getMap())) return;

      console.log('[useBuildingMapLayer] 🔄 Style cambiado — reinyectando layers de edificios');
      layersReady.current = false;

      // Debounce 150 ms para que el nuevo estilo esté completamente aplicado
      window.setTimeout(() => {
        setup();
        void loadForCurrentBbox();
      }, 150);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.on('styledata', handleStyleData as any);

    // ── Inicialización ───────────────────────────────────────────────────────
    const boot = (): void => {
      setup();
      void loadForCurrentBbox();
    };

    if (map.isStyleLoaded()) {
      boot();
    } else {
      let settled = false;
      map.once('load', () => {
        if (!settled) { settled = true; boot(); }
      });
      const poll = setInterval(() => {
        if (map.isStyleLoaded() && !settled) {
          settled = true;
          clearInterval(poll);
          boot();
        }
      }, 200);
      setTimeout(() => clearInterval(poll), 10000);
    }

    return (): void => {
      const m = mapEngine.getMap();
      if (m) {
        m.off('moveend', onMoveEnd);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        m.off('styledata', handleStyleData as any);
        m.off('click',      LAYER_POINTS, handlePointClick);
        if (m.getLayer(LAYER_SELECTED)) m.removeLayer(LAYER_SELECTED);
        if (m.getLayer(LAYER_COUNT))    m.removeLayer(LAYER_COUNT);
        if (m.getLayer(LAYER_POINTS))   m.removeLayer(LAYER_POINTS);
        if (m.getLayer(LAYER_CLUSTERS)) m.removeLayer(LAYER_CLUSTERS);
        if (m.getSource(SOURCE_ID))     m.removeSource(SOURCE_ID);
      }
      layersReady.current = false;
    };
  // filters se maneja por el useEffect dedicado de arriba.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, visible, loadForCurrentBbox]);

  // ── Toggle de visibilidad sin destruir source ─────────────────────────────

  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersReady.current) return;
    const vis = visible ? 'visible' : 'none';
    if (map.getLayer(LAYER_POINTS)) map.setLayoutProperty(LAYER_POINTS, 'visibility', vis);
  }, [visible]);
}
